import { Contest, ContestEntry, JoinedContest } from "interfaces/leaderboard/contest";
import { createAsyncThunk, createSlice, Draft, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "state/store";
import {
  getBetsPath,
  getContestsPath,
  getJoinedContestsPath,
  integratedGetJoinedContests,
  integratedMyBets
} from "utils/backend-path-builders";
import { getRequest } from "utils/httpClient";
import { CONTEST_ENTRY_STATUS } from "utils/constants.ts";
import { getIntegratedAppState } from "./integrated-app-slice";
import { Bet } from "interfaces/bet.ts";
import { getGroupState } from "./group-slice";
import { ContestEntryState } from "utils/contest-utils.ts";

export type LoadingState = "idle" | "loading" | "succeeded" | "failed";

export interface FullContestEntryDetails {
  bets: Bet[];
  loadingState: LoadingState;
  error: string | null;
  calculatedState?: ContestEntryState;
}

export interface ContestsState {
  contests: Contest[];
  joinedContests: JoinedContest[];
  loadingUserContests: boolean;
  loadingContests: boolean;
  error: string | null;
  entryIdToContestId: Record<string, string>;
  contestIdToEntries: Record<string, ContestEntry[]>;
  fullContestEntryDetails: Record<string, FullContestEntryDetails>;
  activeContest?: Contest;
  contestsStale: boolean;
  userContestsStale: boolean;
  newContestEntry?: RecentlyJoinedContestEntry;
  currentContestEntryId?: string;
  contestIdToCurrentEntryId: Record<string, string>;
}

export interface RecentlyJoinedContestEntry {
  id: string;
  balanceHasBeenAnimated: boolean;
  bannerHasBeenShown: boolean;
}

export const initialState: ContestsState = {
  contests: [],
  joinedContests: [],
  error: null,
  loadingUserContests: true,
  loadingContests: true,
  entryIdToContestId: {},
  contestIdToEntries: {},
  fullContestEntryDetails: {},
  contestsStale: true,
  userContestsStale: true,
  currentContestEntryId: undefined,
  contestIdToCurrentEntryId: {},
};

export const fetchContests = createAsyncThunk<
  Contest[],
  string,
  { rejectValue: string }
>("contests/fetchContests", async (groupId, { rejectWithValue }) => {
  const contestsPath = getContestsPath(groupId);

  try {
    return await getRequest(contestsPath, { 
      skipIntegrationApi: true,
      queryParams: { includeDeleted: 'true' }
    });
  } catch (error) {
    console.error(`Error fetching contests`, error);
    return rejectWithValue("Failed to fetch contests");
  }
});

export const fetchUserContests = createAsyncThunk<
  JoinedContest[],
  string,
  { rejectValue: string; state: RootState }
>("contests/fetchUserContests", async (groupId, { rejectWithValue, getState }) => {
  const isIntegratedApp = getIntegratedAppState(getState()).isIntegratedApp;
  const fetchJoinedContestsPath = isIntegratedApp ? integratedGetJoinedContests() : getJoinedContestsPath(groupId);

  try {
    return await getRequest(fetchJoinedContestsPath);
  } catch (error) {
    console.error(`Error fetching contests`, error);
    return rejectWithValue("Failed to fetch contests");
  }
});

export const fetchBetsForContestEntry = createAsyncThunk<
  // Payload now includes bets AND the calculated state
  { contestEntryId: string; bets: Bet[]; calculatedState: ContestEntryState },
  string, // Argument is contestEntryId
  { rejectValue: string; state: RootState }
>("contests/fetchBetsForContestEntry", async (contestEntryId, { rejectWithValue, getState }) => {
  const state = getState();
  const isIntegratedApp = getIntegratedAppState(state).isIntegratedApp;
  const group = getGroupState(state);
  const path = isIntegratedApp ? integratedMyBets(contestEntryId) : getBetsPath(group.id, contestEntryId);

  try {
    const bets: Bet[] = await getRequest(path);
    const calculatedState = calculateContestEntryState(contestEntryId, bets, 3);
    return { contestEntryId, bets, calculatedState };
  } catch (error) {
    console.error(`Error fetching bets for contest entry ${contestEntryId}`, error);
    return rejectWithValue("Failed to fetch bets for contest entry");
  }
});

const slice = createSlice({
  name: "contests",
  initialState,
  reducers: {
    setContestsStale: (state, action) => {
      state.contestsStale = action.payload;
    },
    setUserContestsStale: (state, action) => {
      state.userContestsStale = action.payload;
    },
    clearContestState: () => initialState,

    setNewContestEntryState: (state, action: PayloadAction<RecentlyJoinedContestEntry>) => {
      state.newContestEntry = action.payload;
    },
    setContestCurrentEntry: (state, action: PayloadAction<{ contestId: string; entryId: string }>) => {
      state.contestIdToCurrentEntryId[action.payload.contestId] = action.payload.entryId;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContests.pending, (state: Draft<ContestsState>) => {
        state.loadingContests = true;
        state.error = null;
      })
      .addCase(fetchContests.fulfilled, (state: Draft<ContestsState>, action) => {
        state.loadingContests = false;
        state.error = null;

        state.contests = action.payload.slice().sort((a, b) => {
          return new Date(a.end_at).getTime() - new Date(b.end_at).getTime();
        });
      })
      .addCase(fetchContests.rejected, (state: Draft<ContestsState>, action) => {
        state.loadingContests = false;
        state.error = action.payload as string;
      })
      .addCase(fetchUserContests.pending, (state: Draft<ContestsState>) => {
        state.error = null;
        state.loadingUserContests = true;
      })
      .addCase(fetchUserContests.fulfilled, (state: Draft<ContestsState>, action) => {
        state.entryIdToContestId = {};
        state.contestIdToEntries = {};

        action.payload.forEach((joinedContest) => {
          const contestId: string = joinedContest.contest_id;

          if (!state.contestIdToEntries[contestId]) {
            state.contestIdToEntries[contestId] = [];
          }

          joinedContest.entries.forEach((entry: ContestEntry) => {
            entry.status = toContestStatus(entry.status);

            state.contestIdToEntries[contestId].push(entry);

            state.entryIdToContestId[entry.id] = contestId;
          });
        });

        state.loadingUserContests = false;
        state.error = null;
        state.joinedContests = action.payload;
      })
      .addCase(fetchUserContests.rejected, (state: Draft<ContestsState>, action) => {
        state.error = action.payload as string;
        state.loadingUserContests = false;
      })
      .addCase(fetchBetsForContestEntry.pending, (state: Draft<ContestsState>, action) => {
        const contestEntryId = action.meta.arg;
        const existingDetails = state.fullContestEntryDetails[contestEntryId];
        state.fullContestEntryDetails[contestEntryId] = {
          bets: existingDetails?.bets || [],
          calculatedState: existingDetails?.calculatedState,
          loadingState: "loading",
          error: null,
        } as FullContestEntryDetails;
      })
      .addCase(fetchBetsForContestEntry.fulfilled, (state: Draft<ContestsState>, action) => {
        const { contestEntryId, bets, calculatedState } = action.payload;
        state.fullContestEntryDetails[contestEntryId] = {
          bets: bets,
          calculatedState: calculatedState,
          loadingState: "succeeded",
          error: null,
        } as FullContestEntryDetails;
      })
      .addCase(fetchBetsForContestEntry.rejected, (state: Draft<ContestsState>, action) => {
        const contestEntryId = action.meta.arg;
        const existingDetails = state.fullContestEntryDetails[contestEntryId];
        state.fullContestEntryDetails[contestEntryId] = {
          bets: existingDetails?.bets || [], // Keep existing bets
          calculatedState: undefined, // Clear calculated state on error
          loadingState: "failed",
          error: action.payload as string,
        } as FullContestEntryDetails;
      });
  },
});

export const {
  setContestsStale,
  setUserContestsStale,
  clearContestState,
  setNewContestEntryState,
  setContestCurrentEntry
} = slice.actions;

const toContestStatus = (
  status: string
): CONTEST_ENTRY_STATUS => {
  if (status in CONTEST_ENTRY_STATUS) {
    return status as CONTEST_ENTRY_STATUS;
  }
  throw new Error(`Invalid status: ${status}`);
};

export const getContestsState = (state: RootState): ContestsState => {
  return state.contests;
};

export const getNonDeletedContests = (state: RootState): Contest[] => {
  return getContestsState(state).contests.filter(contest => !contest.is_deleted);
};

export const getContestIdByEntryId = (state: RootState, entryId: string) => {
  return getContestsState(state).entryIdToContestId[entryId];
};

export const getEntriesByContestId = (
  state: RootState,
  contestId: string | undefined
): ContestEntry[] | undefined => {
  return contestId
    ? getContestsState(state).contestIdToEntries[contestId]
    : undefined;
};

export const getContestById = (state: RootState, contestId: string | undefined) => {
  return getContestsState(state).contests.find((contest) => contest.id === contestId);
};

export const getContestEntryById = (state: RootState, contestEntryId: string | undefined): ContestEntry | undefined => {
  if (!contestEntryId) {
    return undefined;
  }
  const contestsState = getContestsState(state);
  const contestId = contestsState.entryIdToContestId[contestEntryId];
  if (!contestId) {
    return undefined;
  }
  const entries = contestsState.contestIdToEntries[contestId];
  if (!entries) {
    return undefined;
  }
  return entries.find(entry => entry.id === contestEntryId);
};

export const getFullContestEntryDetailsById = (state: RootState, contestEntryId: string | undefined): FullContestEntryDetails | undefined => {
  if (!contestEntryId) {
    return undefined;
  }
  return getContestsState(state).fullContestEntryDetails[contestEntryId];
};

export const getCalculatedContestEntryState = (state: RootState, contestEntryId: string | undefined): ContestEntryState | undefined => {
  return getFullContestEntryDetailsById(state, contestEntryId)?.calculatedState;
};

const calculateContestEntryState = (
  contestEntryId: string,
  bets: Bet[],
  minimalNumberOfUniqueContestantsForEntryToBeValid: number = 3
): ContestEntryState => {
  const uniqueContestants = new Set(bets.map(b => b.contestant_name));

  const numberOfPicksOnDistinctContestants = uniqueContestants.size;
  const remainingNumberOfPicksOnDistinctContestantsForEntryToBeValid = minimalNumberOfUniqueContestantsForEntryToBeValid - numberOfPicksOnDistinctContestants;
  const isValid = numberOfPicksOnDistinctContestants >= minimalNumberOfUniqueContestantsForEntryToBeValid;
  const totalNumberOfPicks = bets.length;

  return {
    numberOfPicksOnDistinctContestants,
    minimalNumberOfUniqueContestantsForEntryToBeValid,
    remainingNumberOfPicksOnDistinctContestantsForEntryToBeValid,
    isValid,
    totalNumberOfPicks,
    bets,
    contestEntryId
  };
};

export const getCurrentEntryIdByContestId = (state: RootState, contestId: string | undefined): string | undefined => {
  if (!contestId) {
    return undefined;
  }
  return getContestsState(state).contestIdToCurrentEntryId[contestId];
};

export default slice.reducer;
